<template>
    <v-card variant="flat">
        <v-toolbar flat>
            <v-toolbar-title>{{ t('Pacientes') }}</v-toolbar-title>
            <v-btn fab small color="primary" @click="newPaciente" class="ma-2">
                <v-icon>mdi-plus</v-icon>
            </v-btn>
            <ExportCSV :dataItems="pacientes" />
        </v-toolbar>
        <v-text-field class="ma-4" v-model="search" label="Cerca" prepend-inner-icon="mdi-magnify" single-line
            variant="outlined" hide-details></v-text-field>
        <v-data-table-virtual :headers="headers" :items="pacientes" :search="search" :loading="loading">
            <template v-slot:[`item.action`]="{ item }">
                <v-icon small @click="editPaciente(item)">mdi-pencil</v-icon>
            </template>
            <template v-slot:loading>
                <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
            </template>
        </v-data-table-virtual>
    </v-card>
    <v-dialog v-model="editDialog" persistent>
        <v-card>
            <v-toolbar flat>
                <v-toolbar-title>{{ editedPaciente.id ? t('Editar') : t('Crear') }} {{ t('paciente')
                    }}</v-toolbar-title>
                <v-btn @click="save" prepend-icon="mdi-content-save">
                    {{ t('Guardar') }}
                </v-btn>
                <v-btn icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text>
                <v-text-field v-model="editedPaciente.id" :label="t('ID')" disabled></v-text-field>
                <v-row v-if="editedPaciente.id">
                    <v-col cols="12" class="pa-5">
                        <v-btn block class="ma-3" color="red darken-1" text @click="openDeleteConfirmDialog()">{{
                            t('Eliminar') }} {{ t('paciente') }}</v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </v-dialog>
    <v-dialog v-model="showDeleteConfirmDialog" persistent max-width="500px">
        <v-card>
            <v-card-title>{{ t('Estás_seguro') }}</v-card-title>
            <v-card-text>
                {{ t('Estás_seguro_que_quieres_eliminar') }} {{ t('el_paciente') }} "{{ editedPaciente.id }}"? {{
                    t('Esta_acción_es_irreversible') }}
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="green darken-1" text @click="showDeleteConfirmDialog = false">{{ t('Cancelar') }}</v-btn>
                <v-btn color="red darken-1" text @click="confirmDeletePaciente">{{ t('Eliminar') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import ExportCSV from './ExportCSV.vue'
import { useUiStore } from '@/stores/ui'
import { usePatientStore } from '@/stores/patients'

const { t } = useI18n()
const uiStore = useUiStore()
const patientStore = usePatientStore()

const search = ref('')
const editDialog = ref(false)
const showDeleteConfirmDialog = ref(false)

// Paciente que estamos editando / creando
const editedPaciente = ref({})

// Datos vienen de la store
const pacientes = computed(() => patientStore.items)
const loading = computed(() => patientStore.loading)

// Headers reactivos al idioma
const headers = computed(() => [
  { title: t('ID'), key: 'id' },
  { title: t('Institucion'), key: 'institucion_name' },
  { title: t('Professional'), key: 'created_by_full_name' },
  { title: t('Ecografias'), key: 'n_scans' },       // asegúrate de exponer n_scans en la API
  { title: t('Creado'), key: 'created_at' },
  { title: t('Actualizado'), key: 'updated_at' },
  { title: t('Acciones'), key: 'action', sortable: false },
])

async function loadPacientes() {
  try {
    await patientStore.fetchPatients()
  } catch (err) {
    console.error(err)
    uiStore.triggerSnackbar?.({
      text: t('Error_cargando_pacientes'),
      color: 'error',
    })
  }
}

onMounted(loadPacientes)

// Abrir diálogo para crear paciente
function newPaciente() {
  editedPaciente.value = {}
  editDialog.value = true
}

// Abrir diálogo para editar paciente existente
function editPaciente(item) {
  // item es el objeto de la fila
  editedPaciente.value = { ...item }
  editDialog.value = true
}

// Cerrar diálogo sin guardar
function close() {
  editDialog.value = false
  editedPaciente.value = {}
}

// Guardar: si no hay id → crear; si hay id → actualizar (aunque ahora no tengas campos editables)
async function save() {
  try {
    if (!editedPaciente.value.id) {
      await patientStore.createPatient()
      uiStore.triggerSnackbar?.({
        text: t('Paciente_creado_correctamente'),
        color: 'success',
      })
    } else {
      const { id, ...rest } = editedPaciente.value
      await patientStore.updatePatient(id, rest)
      uiStore.triggerSnackbar?.({
        text: t('Paciente_actualizado_correctamente'),
        color: 'success',
      })
    }
    editDialog.value = false
    editedPaciente.value = {}
  } catch (err) {
    console.error(err)
    uiStore.triggerSnackbar?.({
      text: t('Error_guardando_paciente'),
      color: 'error',
    })
  }
}

// Confirmación de borrado
function openDeleteConfirmDialog() {
  showDeleteConfirmDialog.value = true
}

async function confirmDeletePaciente() {
  try {
    await patientStore.deletePatient(editedPaciente.value.id)
    showDeleteConfirmDialog.value = false
    editDialog.value = false
    uiStore.triggerSnackbar?.({
      text: t('Paciente_eliminado_correctamente'),
      color: 'success',
    })
    editedPaciente.value = {}
  } catch (err) {
    console.error(err)
    uiStore.triggerSnackbar?.({
      text: t('Error_eliminando_paciente'),
      color: 'error',
    })
  }
}
</script>